import { StyleSheet } from 'aphrodite';
import { SPACE, THEME } from '../../../components-v2/Theme/theme';

const card = {
	background: THEME.colors.background.light,
	border: `1px solid ${THEME.colors.border.light}`,
	borderRadius: THEME.common.borderRadius,
	boxSizing: 'border-box',
} as const;

const heading = {
	color: THEME.colors.text.dark,
	fontFamily: THEME.typography.fontFamilies.sora,
	fontSize: THEME.typography.fontSizes.lg,
	fontWeight: THEME.typography.fontWeights.semibold,
	margin: 0,
} as const;

export const styleSheet = StyleSheet.create({
	root: {
		display: 'flex',
		flex: 1,
		flexDirection: 'column',
		gap: SPACE[24],
		minHeight: 0,
		overflowY: 'auto',
		padding: SPACE[24],
		[THEME.queries.sm]: {
			gap: SPACE[16],
			padding: SPACE[16],
		},
	},
	toolbar: {
		alignItems: 'center',
		display: 'flex',
		flexWrap: 'wrap',
		gap: SPACE[16],
		justifyContent: 'flex-end',
	},
	updated: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.xs,
	},
	rangeSelect: {
		width: 180,
		[THEME.queries.sm]: {
			width: '100%',
		},
	},
	rangeTrigger: {
		background: THEME.colors.background.light,
		width: '100%',
	},
	stale: {
		opacity: 0.6,
		transition: 'opacity 160ms ease',
	},
	banner: {
		alignItems: 'center',
		background: THEME.colors.info.light,
		border: `1px solid ${THEME.colors.info.main}`,
		borderRadius: THEME.common.borderRadius,
		display: 'flex',
		flexWrap: 'wrap',
		fontSize: THEME.typography.fontSizes.sm,
		gap: SPACE[12],
		padding: `${SPACE[12]}px ${SPACE[16]}px`,
	},
	bannerText: {
		flex: '1 1 240px',
	},
	tiles: {
		display: 'grid',
		gap: SPACE[16],
		gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
		[THEME.queries.sm]: {
			gap: SPACE[12],
			gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
		},
	},
	tile: {
		...card,
		display: 'flex',
		flexDirection: 'column',
		gap: SPACE[8],
		minHeight: 128,
		padding: SPACE[16],
	},
	tileLabel: {
		alignItems: 'center',
		color: THEME.colors.text.light,
		display: 'flex',
		fontSize: THEME.typography.fontSizes.sm,
		gap: SPACE[4],
	},
	tileValue: {
		color: THEME.colors.text.dark,
		fontFamily: THEME.typography.fontFamilies.sora,
		fontSize: THEME.typography.fontSizes.xxl,
		fontWeight: THEME.typography.fontWeights.semibold,
		lineHeight: '40px',
		[THEME.queries.sm]: {
			fontSize: THEME.typography.fontSizes.xl,
			lineHeight: '32px',
		},
	},
	tileValueMuted: {
		color: THEME.colors.text.light,
	},
	tileCaption: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.xs,
	},
	hintTrigger: {
		alignItems: 'center',
		display: 'inline-flex',
	},
	card: {
		...card,
		display: 'flex',
		flexDirection: 'column',
		gap: SPACE[16],
		padding: SPACE[24],
		[THEME.queries.sm]: {
			padding: SPACE[16],
		},
	},
	cardHeader: {
		alignItems: 'center',
		display: 'flex',
		flexWrap: 'wrap',
		gap: SPACE[16],
		justifyContent: 'space-between',
	},
	heading,
	legend: {
		color: THEME.colors.text.light,
		display: 'flex',
		fontSize: THEME.typography.fontSizes.xs,
		gap: SPACE[16],
	},
	legendItem: {
		alignItems: 'center',
		display: 'flex',
		gap: SPACE[8],
	},
	legendSwatch: {
		borderRadius: THEME.common.borderRadius,
		height: 4,
		width: SPACE[16],
	},
	breakdowns: {
		display: 'grid',
		gap: SPACE[16],
		gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
		[THEME.queries.md]: {
			gridTemplateColumns: 'minmax(0, 1fr)',
		},
	},
	breakdownHeader: {
		color: THEME.colors.text.light,
		display: 'flex',
		fontSize: THEME.typography.fontSizes.xs,
		justifyContent: 'space-between',
	},
	breakdownList: {
		display: 'flex',
		flexDirection: 'column',
		gap: SPACE[12],
		listStyle: 'none',
		margin: 0,
		padding: 0,
	},
	breakdownRow: {
		display: 'flex',
		flexDirection: 'column',
		gap: SPACE[4],
	},
	breakdownLine: {
		display: 'flex',
		fontSize: THEME.typography.fontSizes.sm,
		gap: SPACE[16],
		justifyContent: 'space-between',
	},
	breakdownLabel: {
		...THEME.common.truncate,
	},
	breakdownCount: {
		fontVariantNumeric: 'tabular-nums',
	},
	bar: {
		background: THEME.colors.background.main,
		borderRadius: THEME.common.borderRadius,
		height: 4,
	},
	barFill: {
		background: THEME.colors.primary.main,
		borderRadius: THEME.common.borderRadius,
		height: 4,
	},
	breakdownEmpty: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.sm,
	},
	tooltip: {
		...card,
		display: 'flex',
		flexDirection: 'column',
		fontSize: THEME.typography.fontSizes.xs,
		gap: SPACE[4],
		padding: `${SPACE[8]}px ${SPACE[12]}px`,
	},
	tooltipTitle: {
		fontWeight: THEME.typography.fontWeights.semibold,
	},
	empty: {
		...card,
		alignItems: 'center',
		display: 'flex',
		flexDirection: 'column',
		gap: SPACE[16],
		padding: `${SPACE[64]}px ${SPACE[24]}px`,
		textAlign: 'center',
	},
	emptyIcon: {
		alignItems: 'center',
		background: THEME.colors.background.main,
		borderRadius: '50%',
		display: 'flex',
		height: 72,
		justifyContent: 'center',
		width: 72,
	},
	emptyTitle: {
		...heading,
		fontSize: THEME.typography.fontSizes.xl,
	},
	emptyBody: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.md,
		lineHeight: '24px',
		margin: 0,
		maxWidth: 520,
	},
	emptyNote: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.sm,
		margin: 0,
		maxWidth: 520,
	},
	skeletonTile: {
		height: 128,
	},
	skeletonChart: {
		height: 320,
	},
});
